// src/lib/jobLabel.js
// Compact JOB LABEL to stick on the package (4x6"): logo, Job #, client, name,
// scannable QR, date and total pieces. Printable PDF (jsPDF). English.
import { jsPDF } from "jspdf";
import QRCode from "qrcode";

const totalPieces = (sheets = []) =>
  sheets.reduce((sum, s) => {
    const reps = Number(s.repetitions) || 0;
    const per = (s.pieces || []).reduce((a, p) => a + (Number(p.qtyPerSheet) || 0), 0);
    return sum + per * reps;
  }, 0);

// Load a same-origin image (e.g. /public logo) as dataURL for jsPDF.
async function loadLocalImage(url) {
  try {
    const resp = await fetch(url);
    if (!resp.ok) return null;
    const blob = await resp.blob();
    const dataUrl = await new Promise((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(fr.result);
      fr.onerror = rej;
      fr.readAsDataURL(blob);
    });
    const m = /^data:image\/(\w+);/.exec(dataUrl || "");
    let fmt = (m ? m[1] : "png").toUpperCase();
    if (fmt === "JPG") fmt = "JPEG";
    const dims = await new Promise((res) => {
      const im = new Image();
      im.onload = () => res({ w: im.naturalWidth || 1, h: im.naturalHeight || 1 });
      im.onerror = () => res({ w: 1, h: 1 });
      im.src = dataUrl;
    });
    return { dataUrl, format: fmt, w: dims.w, h: dims.h };
  } catch (_) { return null; }
}

export async function buildJobLabelPdf({ jobId, jobData = {}, client, sheets = [] }) {
  const W = 288, H = 432, M = 18; // 4 x 6 inches (pt)
  const doc = new jsPDF({ unit: "pt", format: [W, H] });
  const center = W / 2;

  // Logo (CARTONBAR, black) from /public — same origin, loads fine.
  const logo = await loadLocalImage(`${process.env.PUBLIC_URL || ""}/cartonbar-logo.png`);
  if (logo) {
    const lw = 130, lh = Math.min(40, lw * (logo.h / logo.w));
    try { doc.addImage(logo.dataUrl, logo.format, center - lw / 2, M, lw, lh); } catch (_) {}
  } else {
    doc.setFont("helvetica", "bold"); doc.setFontSize(14);
    doc.text("CARTONBAR", center, M + 16, { align: "center" });
  }
  doc.setDrawColor(0); doc.setLineWidth(1); doc.line(M, M + 48, W - M, M + 48);

  // Job # (large)
  doc.setFont("helvetica", "bold"); doc.setFontSize(34);
  doc.text(String(jobData.id || jobId || ""), center, M + 86, { align: "center" });

  // Client + job name
  doc.setFont("helvetica", "bold"); doc.setFontSize(13);
  doc.text(doc.splitTextToSize((client?.name || jobData.clientName || "—").toUpperCase(), W - 2 * M), center, M + 110, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(11);
  doc.text(doc.splitTextToSize(jobData.jobName || "", W - 2 * M), center, M + 128, { align: "center" });

  // QR (scan to open the job)
  try {
    const qr = await QRCode.toDataURL(JSON.stringify({ jobId: jobData.id || jobId }), { margin: 1, width: 320 });
    const qrSize = 140;
    doc.addImage(qr, "PNG", center - qrSize / 2, M + 144, qrSize, qrSize);
  } catch (_) { /* no QR */ }

  // Footer data (English)
  let y = M + 144 + 140 + 26;
  doc.setDrawColor(200); doc.line(M, y - 14, W - M, y - 14);
  doc.setFontSize(11);
  const fecha = (() => { try { return jobData.deliveryDate || (jobData.createdAt?.toDate ? jobData.createdAt.toDate().toLocaleDateString() : ""); } catch (_) { return ""; } })();
  const piezas = totalPieces(sheets);
  doc.setFont("helvetica", "bold"); doc.text("Pieces:", M, y); doc.setFont("helvetica", "normal"); doc.text(String(piezas), M + 55, y);
  y += 18;
  doc.setFont("helvetica", "bold"); doc.text("Delivery:", M, y); doc.setFont("helvetica", "normal"); doc.text(String(fecha || "—"), M + 60, y);
  y += 18;
  doc.setFont("helvetica", "bold"); doc.text("Box:", M, y); doc.setFont("helvetica", "normal"); doc.text("____ of ____", M + 35, y);

  doc.save(`LABEL_${jobData.id || jobId}.pdf`);
}
